<template>
  <div>
    <div class="flex justify-between items-center mb-3">
      <p class="text-sm text-gray-500">
        {{ $t("message.image_generator.templates_table_hint") }}
      </p>
      <button class="border rounded px-2 py-1 text-xs" @click="load()">
        <i class="mdi mdi-refresh"></i>
      </button>
    </div>

    <div
      v-if="loading"
      class="border border-dashed rounded p-10 text-center text-gray-400"
    >
      <i class="mdi mdi-loading mdi-spin text-3xl"></i>
    </div>
    <div
      v-else-if="!templates.length"
      class="border border-dashed rounded p-10 text-center text-gray-400"
    >
      {{ $t("message.image_generator.no_templates") }}
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-xs border">
        <thead class="bg-gray-100">
          <tr>
            <th class="text-left p-2">
              {{ $t("message.image_generator.template_name") }}
            </th>
            <th class="text-left p-2">
              {{ $t("message.image_generator.prompt") }}
            </th>
            <th class="text-left p-2">
              {{ $t("message.image_generator.aspect_ratio") }}
            </th>
            <th class="text-left p-2">
              {{ $t("message.image_generator.size") }}
            </th>
            <th class="text-left p-2">
              {{ $t("message.image_generator.count") }}
            </th>
            <th class="text-left p-2">
              {{ $t("message.image_generator.style_assets") }}
            </th>
            <th class="p-2"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="template in templates">
            <tr :key="template.id" class="border-t">
              <td class="p-2 font-semibold align-top">{{ template.name }}</td>
              <td class="p-2 align-top max-w-md">
                <span class="text-gray-600">{{ template.prompt }}</span>
              </td>
              <td class="p-2 align-top">{{ template.aspectRatio || "-" }}</td>
              <td class="p-2 align-top">{{ template.imageSize || "-" }}</td>
              <td class="p-2 align-top">{{ template.imagesCount || "-" }}</td>
              <td class="p-2 align-top">{{ assetNames(template) }}</td>
              <td class="p-2 align-top whitespace-nowrap">
                <button
                  class="border rounded px-2 py-1 mr-1 hover:bg-gray-100"
                  @click="$emit('apply', parse(template))"
                >
                  {{ $t("message.image_generator.use") }}
                </button>
                <button
                  class="border rounded px-2 py-1 mr-1 hover:bg-gray-100"
                  @click="edit(template)"
                >
                  <i class="mdi mdi-pencil"></i>
                </button>
                <button
                  class="border rounded px-2 py-1 hover:bg-gray-100 text-red-600"
                  @click="remove(template)"
                >
                  <i class="mdi mdi-delete"></i>
                </button>
              </td>
            </tr>
            <tr
              v-if="editing && editing.id === template.id"
              :key="`edit-${template.id}`"
              class="bg-gray-50 border-t"
            >
              <td colspan="7" class="p-3">
                <input
                  v-model="editing.name"
                  type="text"
                  class="regular clear full mb-2"
                  :placeholder="$t('message.image_generator.template_name')"
                />
                <textarea
                  v-model="editing.prompt"
                  rows="4"
                  class="regular clear full mb-2"
                ></textarea>
                <div class="grid grid-cols-3 gap-2 mb-2">
                  <input
                    v-model="editing.aspectRatio"
                    type="text"
                    class="regular clear full"
                    placeholder="1:1"
                  />
                  <input
                    v-model="editing.imageSize"
                    type="text"
                    class="regular clear full"
                    placeholder="1K"
                  />
                  <input
                    v-model.number="editing.imagesCount"
                    type="number"
                    min="1"
                    max="4"
                    class="regular clear full"
                  />
                </div>
                <button
                  class="bg-gray-800 text-white rounded px-3 py-1 mr-2"
                  :disabled="saving"
                  @click="save()"
                >
                  {{
                    saving
                      ? $t("message.image_generator.saving")
                      : $t("message.image_generator.save_template")
                  }}
                </button>
                <button
                  class="border rounded px-3 py-1"
                  @click="editing = null"
                >
                  {{ $t("message.image_generator.cancel") }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getPromptTemplates,
  updatePromptTemplate,
  deletePromptTemplate,
  getImageAssets
} from "@/api/store/AiService.js";

export default {
  name: "AiPromptTemplatesTable",
  data() {
    return {
      templates: [],
      assets: [],
      loading: false,
      saving: false,
      editing: null
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [templatesData, assetsData] = await Promise.all([
          getPromptTemplates(),
          getImageAssets()
        ]);
        this.templates = (templatesData.templates || []).map(t => ({
          ...t,
          id: Number(t.id)
        }));
        this.assets = assetsData.assets || [];
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.templates_error"));
      } finally {
        this.loading = false;
      }
    },
    // asset_ids se guarda como JSON en una columna de texto.
    parsedIds(template) {
      try {
        return template.assetIds ? JSON.parse(template.assetIds) : [];
      } catch (e) {
        return [];
      }
    },
    assetNames(template) {
      const ids = this.parsedIds(template);
      if (!ids.length) return "-";
      return ids
        .map(id => {
          const asset = this.assets.find(a => Number(a.id) === id);
          return asset ? asset.name : `#${id}`;
        })
        .join(", ");
    },
    parse(template) {
      return {
        id: template.id,
        prompt: template.prompt,
        aspectRatio: template.aspectRatio,
        imageSize: template.imageSize,
        count: template.imagesCount ? Number(template.imagesCount) : null,
        assetIds: this.parsedIds(template)
      };
    },
    edit(template) {
      this.editing = {
        id: template.id,
        name: template.name,
        prompt: template.prompt,
        aspectRatio: template.aspectRatio,
        imageSize: template.imageSize,
        imagesCount: template.imagesCount ? Number(template.imagesCount) : null
      };
    },
    async save() {
      this.saving = true;
      try {
        await updatePromptTemplate(this.editing.id, {
          name: this.editing.name,
          prompt: this.editing.prompt,
          aspectRatio: this.editing.aspectRatio,
          imageSize: this.editing.imageSize,
          imagesCount: this.editing.imagesCount
        });
        this.editing = null;
        await this.load();
        this.$emit("changed");
        this.$toast.success(this.$t("message.image_generator.template_saved"));
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.templates_error"));
      } finally {
        this.saving = false;
      }
    },
    async remove(template) {
      if (
        !confirm(this.$t("message.image_generator.confirm_delete_template"))
      ) {
        return;
      }
      try {
        await deletePromptTemplate(template.id);
        this.templates = this.templates.filter(t => t.id !== template.id);
        this.$emit("changed");
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.templates_error"));
      }
    }
  }
};
</script>
